import { http } from "./client";
import type { PageParams } from "@/types/api";
import type { Branch, BranchAssignment, BranchSummary, EntityStatus } from "@/types/domain";

export interface BranchFilters extends PageParams {
  search?: string;
  status?: EntityStatus | "";
}

export type BranchInput = Partial<
  Pick<
    Branch,
    | "code"
    | "name"
    | "branch_type"
    | "description"
    | "address"
    | "city"
    | "state"
    | "country"
    | "pincode"
    | "phone"
    | "email"
    | "branch_head_id"
    | "latitude"
    | "longitude"
    | "time_zone"
    | "working_hours"
    | "weekly_off"
    | "cost_center"
    | "status"
  >
>;

/** One department as a branch lists it (GET /branches/:id/departments). */
export interface BranchDepartment {
  id: string;
  name: string;
  code: string;
  status: EntityStatus;
  head_employee_id?: string;
  head?: { id: string; full_name: string; employee_code: string };
  /** Added to this branch explicitly (so it can be removed again). A
   * department can also be listed only because employees work in it here. */
  assigned: boolean;
  employee_count: number;
  designation_count: number;
}

export const branchesApi = {
  list: (params: BranchFilters = {}) => http.getPage<Branch>("/branches", params),
  get: (id: string) => http.get<Branch>(`/branches/${id}`),
  create: (input: BranchInput) => http.post<Branch>("/branches", input),
  update: (id: string, input: BranchInput) => http.put<Branch>(`/branches/${id}`, input),
  setStatus: (id: string, status: EntityStatus) => http.patch<Branch>(`/branches/${id}/status`, { status }),
  // What has to move before the branch can be deleted.
  deletePreview: (id: string) => http.get<BranchDeletePreview>(`/branches/${id}/delete-preview`),
  // With transfer_to, the branch's employees (with a transfer history row),
  // user access, devices and gateways move there first; without it the
  // delete is refused (409) while anything still belongs to the branch.
  remove: (id: string, opts: { transfer_to?: string; reason?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.transfer_to) qs.set("transfer_to", opts.transfer_to);
    if (opts.reason) qs.set("reason", opts.reason);
    const q = qs.toString();
    return http.delete<void>(`/branches/${id}${q ? `?${q}` : ""}`);
  },
  summary: () => http.get<BranchSummary>("/branches/summary"),
  // A Branch Manager's self-scope (mirrors departmentsApi's my-teams) - the
  // branch this user heads. 404s for anyone who heads no branch (including
  // a single-branch tenant's Tenant Admin, who has no need for this - they
  // already see their one branch via the ordinary list/detail routes).
  myBranch: () => http.get<Branch>("/branches/my-branch"),
  headcount: () => http.get<{ by_branch: Record<string, number> }>("/branches/headcount"),
  // Branch-scope ABAC admin surface (migration 0115) - who is explicitly
  // granted visibility into this branch's employees/attendance/leave. GET
  // only needs branch.read (same as the rest of this module); POST/DELETE
  // require branch.write, matching router.go's gating exactly.
  departments: {
    list: (branchId: string, params: { search?: string; status?: EntityStatus | "" } = {}) =>
      http.get<BranchDepartment[]>(`/branches/${branchId}/departments`, params),
    assign: (branchId: string, departmentId: string) =>
      http.post<{ branch_id: string; department_id: string }>(`/branches/${branchId}/departments`, { department_id: departmentId }),
    unassign: (branchId: string, departmentId: string) => http.delete<unknown>(`/branches/${branchId}/departments/${departmentId}`),
  },
  assignments: {
    list: (branchId: string) => http.get<{ assignments: BranchAssignment[] }>(`/branches/${branchId}/assignments`),
    add: (branchId: string, userId: string) =>
      http.post<{ assignment: BranchAssignment }>(`/branches/${branchId}/assignments`, { user_id: userId }),
    remove: (branchId: string, userId: string) => http.delete<void>(`/branches/${branchId}/assignments/${userId}`),
  },
};

export interface BranchDeleteItem {
  id: string;
  name: string;
  detail: string;
}

export interface BranchDeletePreview {
  branch_id: string;
  branch_name: string;
  branch_code: string;
  employees: BranchDeleteItem[];
  users: BranchDeleteItem[];
  devices: BranchDeleteItem[];
  gateways: BranchDeleteItem[];
}
